/** Shared helpers for nested municipality document groups (parent_id + position). */

export interface DocumentGroupTreeFields {
  id: number;
  parent_id?: number | null;
  position: number;
  document_ids: number[];
}

export function indexGroupsById<T extends { id: number }>(groups: T[]): Map<number, T> {
  return new Map(groups.map((g) => [g.id, g]));
}

export function buildChildrenByParentId<
  T extends { id: number; parent_id?: number | null; position?: number },
>(groups: readonly T[]): Map<number | null, T[]> {
  const map = new Map<number | null, T[]>();
  for (const g of groups) {
    const pid = g.parent_id ?? null;
    const arr = map.get(pid) ?? [];
    arr.push(g);
    map.set(pid, arr);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);
  }
  return map;
}

/**
 * All document IDs in this group's subtree (direct + descendants), stable order, deduped.
 */
export function subtreeDocumentIds<T extends DocumentGroupTreeFields>(
  groupId: number,
  groupsById: Map<number, T>,
  childrenByParent: Map<number | null, T[]>,
  memo: Map<number, number[]> = new Map(),
): number[] {
  if (memo.has(groupId)) {
    return memo.get(groupId)!;
  }
  const g = groupsById.get(groupId);
  if (!g) {
    memo.set(groupId, []);
    return [];
  }
  const seen = new Set<number>(g.document_ids);
  const ordered: number[] = [...g.document_ids];
  for (const ch of childrenByParent.get(groupId) ?? []) {
    for (const docId of subtreeDocumentIds(ch.id, groupsById, childrenByParent, memo)) {
      if (!seen.has(docId)) {
        seen.add(docId);
        ordered.push(docId);
      }
    }
  }
  memo.set(groupId, ordered);
  return ordered;
}

export interface DocumentNameFields {
  id: number;
  name: string;
  description?: string | null;
}

export interface GroupSearchFields {
  name: string;
  description?: string | null;
}

export function documentsInIdOrder<T extends { id: number }>(
  documentsById: Map<number, T>,
  documentIds: number[],
): T[] {
  const ordered: T[] = [];
  for (const id of documentIds) {
    const doc = documentsById.get(id);
    if (doc) ordered.push(doc);
  }
  return ordered;
}

/** Cap on document rows mounted per list while a search is active; the rest is summarized. */
export const SEARCH_RENDER_LIMIT = 100;

export function limitSearchResults<T>(
  items: T[],
  limit = SEARCH_RENDER_LIMIT,
): { visible: T[]; hiddenCount: number } {
  if (items.length <= limit) return { visible: items, hiddenCount: 0 };
  return { visible: items.slice(0, limit), hiddenCount: items.length - limit };
}

/** Lowercased name plus description, built once per document so a keystroke does not re-lowercase the corpus. */
export function documentSearchHaystack(doc: DocumentNameFields): string {
  if (!doc.description) return doc.name.toLowerCase();
  return `${doc.name}\n${doc.description}`.toLowerCase();
}

export function documentMatchesSearch(
  doc: DocumentNameFields,
  normalizedQuery: string,
  haystack?: string,
): boolean {
  if (!normalizedQuery) return true;
  return (haystack ?? documentSearchHaystack(doc)).includes(normalizedQuery);
}

export function groupMatchesQuery(group: GroupSearchFields, normalizedQuery: string): boolean {
  if (!normalizedQuery) return true;
  if (group.name.toLowerCase().includes(normalizedQuery)) return true;
  return group.description?.toLowerCase().includes(normalizedQuery) ?? false;
}

export function groupIsSearchMatch<T extends DocumentGroupTreeFields & GroupSearchFields>(
  groupId: number,
  normalizedQuery: string,
  groupsById: Map<number, T>,
  childrenByParent: Map<number | null, T[]>,
  documentsById: Map<number, DocumentNameFields>,
  cache: Map<number, boolean> = new Map(),
  haystackById?: Map<number, string>,
): boolean {
  if (!normalizedQuery) return true;
  const cached = cache.get(groupId);
  if (cached !== undefined) return cached;
  const group = groupsById.get(groupId);
  if (!group) {
    cache.set(groupId, false);
    return false;
  }
  const matches =
    groupMatchesQuery(group, normalizedQuery) ||
    group.document_ids.some((id) => {
      const doc = documentsById.get(id);
      return doc ? documentMatchesSearch(doc, normalizedQuery, haystackById?.get(id)) : false;
    }) ||
    (childrenByParent.get(groupId) ?? []).some((child) =>
      groupIsSearchMatch(
        child.id,
        normalizedQuery,
        groupsById,
        childrenByParent,
        documentsById,
        cache,
        haystackById,
      ),
    );
  cache.set(groupId, matches);
  return matches;
}

export function allGroupedDocumentIds<T extends DocumentGroupTreeFields>(
  groups: T[],
  groupsById: Map<number, T>,
  childrenByParent: Map<number | null, T[]>,
): Set<number> {
  const memo = new Map<number, number[]>();
  const out = new Set<number>();
  for (const g of groups) {
    for (const id of subtreeDocumentIds(g.id, groupsById, childrenByParent, memo)) {
      out.add(id);
    }
  }
  return out;
}
